import { Injectable } from '@angular/core';
import { User } from '../models/user';
import { AuthService } from './auth.service';

/**
 * Stage 5 PR 91, 2026-05-18 — Ported from Atenza 2026-05-21 B14.PERMS-C.
 *
 * Replaces the 425-line PR 10 / PR 10.2 switch-on-section implementation
 * with Atenza's dynamic column-lookup version.
 *
 * Why the switch had to go:
 *
 *   1. It silently returned `false` for any column the operator added
 *      to a BE template but forgot to add a case for here. Several
 *      columns were quietly broken at removal time — same bug class
 *      as Atenza's BM-FE (`benefits_maintenance` invisible until
 *      validation rule + FE field shipped together) and the earlier
 *      `mobile_banners` / `integrations` incidents from PR 4.1 → 10.
 *
 *   2. It carried PR 10.2's "SUPER_ADMIN bypass" — operator could test
 *      the whole UI without ever exercising the column gate, hiding
 *      bugs where a custom-role user's permission was missing or
 *      wrong. Per the operator directive 2026-05-21 ("if you have
 *      no permission you cannot see it, even as SUPER_ADMIN") the
 *      bypass is gone.
 *
 * The new shape just reads the column dynamically from the user's
 * permissions object. If the backend exposes a column on
 * `user.permissions`, the FE check sees it automatically — no
 * additional code maintenance. Permissions are now driven entirely by
 * what the BE serializer exposes, with the new BillingIntegrity /
 * BenefitsMaintenance columns picked up automatically by this lookup.
 *
 * Fail-closed behaviour preserved: an unknown section name (typo in
 * a template, or a column the BE doesn't expose) returns false. The
 * null-user guard (PR 10.2 hotfix) is preserved too — directives that
 * mount before login don't blow up, they just return false.
 *
 * Companion change: the BE sweep that replaces hardcoded
 * SUPER_ADMIN/ADMIN role checks with PermissionGate::canDoAtLevel()
 * lands in Round 4 (PR 92). Pair of changes lands the architectural
 * rule end-to-end.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  public user!: User;

  constructor(private authService: AuthService) {
    this.user = this.authService.getCurrentUserFromLocal();
  }

  checkPermissionsForSection(section: string, permission: number): boolean {
    if (!this.user?.permissions) {
      return false;
    }
    const value = (this.user.permissions as any)[section];
    return typeof value === 'number' && value >= permission;
  }
}
